import { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";

interface TranslationCacheEntry {
  text: string;
  translated: string;
  timestamp: number;
}

const TRANSLATION_CACHE_KEY = "b2b_translation_cache";
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function getCache(): Record<string, TranslationCacheEntry> {
  try {
    const raw = localStorage.getItem(TRANSLATION_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function setCache(cache: Record<string, TranslationCacheEntry>) {
  try {
    localStorage.setItem(TRANSLATION_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // If localStorage is full, clear old entries
    const entries = Object.entries(cache).sort((a, b) => b[1].timestamp - a[1].timestamp);
    const trimmed = entries.slice(0, Math.floor(entries.length / 2));
    const trimmedObj = Object.fromEntries(trimmed);
    try {
      localStorage.setItem(TRANSLATION_CACHE_KEY, JSON.stringify(trimmedObj));
    } catch {
      // give up
    }
  }
}

function getCached(text: string): string | null {
  const cache = getCache();
  const entry = cache[text];
  if (entry && Date.now() - entry.timestamp < CACHE_TTL_MS) {
    return entry.translated;
  }
  return null;
}

function setCached(text: string, translated: string) {
  const cache = getCache();
  cache[text] = { text, translated, timestamp: Date.now() };
  setCache(cache);
}

/**
 * Fetches AI translation (Vietnamese → Korean) via Supabase Edge Function.
 * Uses browser-side cache to avoid redundant API calls.
 */
export async function translateText(text: string): Promise<string> {
  if (!text || text.trim().length === 0) return "";

  const cached = getCached(text);
  if (cached) return cached;

  try {
    const { data, error } = await supabase.functions.invoke("auto-translate", {
      body: { text, sourceLang: "vi", targetLang: "ko" },
    });
    if (error || !data?.translated) {
      throw new Error(error?.message || "Translation failed");
    }
    setCached(text, data.translated);
    return data.translated;
  } catch {
    return text; // fallback: return original Vietnamese
  }
}

/**
 * Hook: auto-translate a single string when isKr=true and krValue is empty.
 */
export function useTranslatedText(viValue: string, krValue?: string | null, isKr?: boolean) {
  const [displayValue, setDisplayValue] = useState(krValue || viValue || "");
  const pendingRef = useRef(false);

  useEffect(() => {
    if (!isKr || !viValue || viValue.trim().length === 0) {
      setDisplayValue(viValue || "");
      return;
    }

    if (krValue && krValue.trim().length > 0) {
      setDisplayValue(krValue);
      return;
    }

    // Need auto-translation
    if (pendingRef.current) return;
    pendingRef.current = true;

    translateText(viValue)
      .then((translated) => {
        setDisplayValue(translated);
      })
      .finally(() => {
        pendingRef.current = false;
      });
  }, [viValue, krValue, isKr]);

  return displayValue;
}

/**
 * Hook: auto-translate an array of strings (e.g. tags).
 */
export function useTranslatedArray(
  viValues: string[],
  krValues?: string[] | null,
  isKr?: boolean
): string[] {
  const [displayValues, setDisplayValues] = useState<string[]>(
    krValues?.length ? krValues : viValues
  );

  useEffect(() => {
    if (!isKr || !viValues || viValues.length === 0) {
      setDisplayValues(viValues || []);
      return;
    }

    if (krValues && krValues.length > 0) {
      setDisplayValues(krValues);
      return;
    }

    // Need auto-translation for each tag
    const toTranslate = viValues.filter((t) => t && t.trim().length > 0);
    if (toTranslate.length === 0) {
      setDisplayValues(viValues);
      return;
    }

    Promise.all(toTranslate.map((t) => translateText(t).catch(() => t)))
      .then((results) => {
        setDisplayValues(results);
      })
      .catch(() => {
        setDisplayValues(viValues);
      });
  }, [viValues, krValues, isKr]);

  return displayValues;
}

/**
 * Hook: auto-translate a spec array (array of {label, value}).
 */
export function useTranslatedSpecs(
  viSpecs?: { label: string; value: string }[] | null,
  krSpecs?: { label: string; value: string }[] | null,
  isKr?: boolean
): { label: string; value: string }[] {
  const [displaySpecs, setDisplaySpecs] = useState<
    { label: string; value: string }[]
  >(krSpecs?.length ? krSpecs : viSpecs || []);

  useEffect(() => {
    if (!isKr || !viSpecs || viSpecs.length === 0) {
      setDisplaySpecs(viSpecs || []);
      return;
    }

    if (krSpecs && krSpecs.length > 0) {
      setDisplaySpecs(krSpecs);
      return;
    }

    // Translate each spec label and value
    Promise.all(
      viSpecs.map(async (s) => {
        const [label, value] = await Promise.all([
          translateText(s.label).catch(() => s.label),
          translateText(s.value).catch(() => s.value),
        ]);
        return { label, value };
      })
    )
      .then((results) => {
        setDisplaySpecs(results);
      })
      .catch(() => {
        setDisplaySpecs(viSpecs);
      });
  }, [viSpecs, krSpecs, isKr]);

  return displaySpecs;
}